/**
 * The load state machine as the portal reads it (design 5.1), pure. The DB enum `load_status` is
 * snake_case and append-only (contract-app 9): a value this portal does not know maps to `null` here and
 * to the neutral `unknown` chip on screen, never to a guessed status.
 */

/** `load_status`, in the enum's order (camelCase, the vocabulary of the tokens and the copy). */
export const LOAD_STATUSES = [
  'potential',
  'booked',
  'dispatched',
  'accepted',
  'atPickup',
  'loaded',
  'inTransit',
  'delivered',
  'podReceived',
  'invoiced',
  'paid',
  'canceled',
] as const;
export type LoadStatus = (typeof LOAD_STATUSES)[number];
/** A status as the screens hold it: a known one, or `unknown` (read only, no actions). */
export type LoadStatusOrUnknown = LoadStatus | 'unknown';

const toSnake = (s: string) => s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
const DB_OF = new Map<LoadStatus, string>(LOAD_STATUSES.map((s) => [s, toSnake(s)]));
const FROM_DB = new Map<string, LoadStatus>(LOAD_STATUSES.map((s) => [toSnake(s), s]));

/** DB text -> LoadStatus; anything else (a newer enum value, a camelCase string, a non-string) -> null. */
export function fromDb(value: unknown): LoadStatus | null {
  return typeof value === 'string' ? (FROM_DB.get(value) ?? null) : null;
}

/** LoadStatus -> the DB text an RPC takes (`load_advance(p_to)`). */
export function toDb(status: LoadStatus): string {
  return DB_OF.get(status)!;
}

const RANK = new Map<LoadStatus, number>(LOAD_STATUSES.map((s, i) => [s, i]));
export const rankOf = (s: LoadStatus): number => RANK.get(s)!;

/** potential..pod_received: the loads the office still works on (the inFlight view). */
export function isInFlight(status: LoadStatus): boolean {
  return rankOf(status) <= rankOf('podReceived');
}

/** The DB texts of each read view (design 3.2). */
export const IN_FLIGHT_DB = LOAD_STATUSES.filter(isInFlight).map(toDb);
export const CLOSED_DB = ['invoiced', 'paid', 'canceled'];

export const LOAD_TABS = ['active', 'delivered', 'billing', 'closed', 'all'] as const;
export type LoadTab = (typeof LOAD_TABS)[number];

/** The list tab a status belongs to (every status is in exactly one, plus `all`). */
export function tabOf(status: LoadStatus): Exclude<LoadTab, 'all'> {
  if (rankOf(status) <= rankOf('inTransit')) return 'active';
  if (status === 'delivered') return 'delivered';
  if (status === 'podReceived' || status === 'invoiced') return 'billing';
  return 'closed';
}

/** The next road step (by phone) from each road status: accepted -> at_pickup -> ... -> delivered. */
export const ROAD_NEXT: Partial<Record<LoadStatus, LoadStatus>> = {
  accepted: 'atPickup',
  atPickup: 'loaded',
  loaded: 'inTransit',
  inTransit: 'delivered',
};

export const LOAD_ACTION_IDS = [
  'book',
  'dispatch',
  'accept',
  'decline',
  'atPickup',
  'loaded',
  'inTransit',
  'delivered',
  // audit-c (Q2): "Delivered (date/time)", load_advance_to: the road chained to delivered in one call.
  'deliveredAt',
  'podReceived',
  'uploadPod',
  'price',
  'reprice',
  'invoiceBroker',
  'editDetails',
  'unassign',
  'cancel',
] as const;
export type LoadActionId = (typeof LOAD_ACTION_IDS)[number];

export const BLOCK_KEYS = ['noStops', 'noRate', 'noPlan', 'receivable', 'rolling'] as const;
export type BlockKey = (typeof BLOCK_KEYS)[number];

export interface LoadAction {
  id: LoadActionId;
  /** `menu`: a plain item of the overflow menu (Edit details); `destructive`: a red item of that menu. */
  variant: 'primary' | 'secondary' | 'menu' | 'destructive';
  /** Why the action is disabled (`loadActions.block.<key>`), shown as text under it; null = enabled. */
  block: BlockKey | null;
}

export interface ActionContext {
  /** `can('load.run')`: admin, manager, dispatcher. Without it nothing is offered (accounting). */
  canRun: boolean;
  /** At least one pickup and one delivery (load_dispatch's precondition). */
  hasStops: boolean;
  /** A live POD document (Q3: its absence is a confirm, never a block). */
  hasPodDoc: boolean;
  hasLinehaul: boolean;
  /** A dispatch plan exists (truck or carrier). `null` = the reader may not see plans: unknown. */
  hasPlan: boolean | null;
  /** The load is invoiced to the broker (a `load_receivables` row). */
  hasReceivable: boolean;
  /** A canceled load carries a TONU (its fee was earned on it): Reprice edits the TONU (fee-overrides). */
  hasTonu?: boolean;
  /** Any stop carries an arrival or departure stamp: the load is rolling and Remove driver is refused (load_unassign 55000). */
  hasStamps?: boolean;
}

/**
 * The ordered actions the header offers (design 5.1 table). UI gating is courtesy: every action's RPC
 * still maps its 42501.
 */
export function actionsFor(load: { status: LoadStatusOrUnknown }, ctx: ActionContext): LoadAction[] {
  if (!ctx.canRun || load.status === 'unknown') return [];
  const a = (id: LoadActionId, variant: LoadAction['variant'], block: BlockKey | null = null): LoadAction => ({ id, variant, block });
  const dispatchBlock = ctx.hasStops ? null : 'noStops';
  const podBlock: BlockKey | null = !ctx.hasLinehaul ? 'noRate' : ctx.hasPlan === false ? 'noPlan' : null;
  const cancel = a('cancel', 'destructive', ctx.hasReceivable ? 'receivable' : null);
  // Remove driver (load_unassign): back to booked with no driver or truck; refused once a stop is stamped.
  const unassign = a('unassign', 'destructive', ctx.hasStamps ? 'rolling' : null);
  // Edit details (load_update, 0079): the header fields, on every load the RPC does not refuse (paid, canceled).
  const edit = a('editDetails', 'menu');
  const status = load.status;

  switch (status) {
    case 'potential':
      return [a('book', 'primary'), a('dispatch', 'secondary', dispatchBlock), a('price', 'secondary'), edit, cancel];
    case 'booked':
      return [a('dispatch', 'primary', dispatchBlock), a('price', 'secondary'), edit, cancel];
    case 'dispatched':
      return [a('accept', 'primary'), a('decline', 'secondary'), a('price', 'secondary'), edit, unassign, cancel];
    // audit-c (Q2, D-G and coordinator decision 3): "Delivered (date/time)" from accepted, at pickup and loaded, so a
    // phoned load is 2 dialogs (Delivered + POD), or 3 with a real pickup time (At pickup, Delivered, POD).
    case 'accepted':
      return [a('atPickup', 'primary'), a('deliveredAt', 'secondary'), a('price', 'secondary'), edit, unassign, cancel];
    case 'atPickup':
    case 'loaded':
      return [a(ROAD_NEXT[status] as LoadActionId, 'primary'), a('deliveredAt', 'secondary'), a('price', 'secondary'), edit, cancel];
    case 'inTransit':
      return [a(ROAD_NEXT[status] as LoadActionId, 'primary'), a('price', 'secondary'), edit, cancel];
    case 'delivered':
      return [a('podReceived', 'primary', podBlock), a('uploadPod', 'secondary'), a('price', 'secondary'), edit, cancel];
    case 'podReceived':
      return [a('reprice', 'secondary'), a('invoiceBroker', 'secondary'), edit, cancel];
    case 'invoiced':
      return [a('reprice', 'secondary'), edit, cancel];
    case 'canceled':
      // 0183: a TONU is billed to the broker like a POD-received load, until it has its receivable.
      if (!ctx.hasTonu) return [];
      return ctx.hasReceivable ? [a('reprice', 'secondary')] : [a('reprice', 'secondary'), a('invoiceBroker', 'secondary')];
    case 'paid':
      return [];
  }
}

/** Before the fee is earned (load_price); from the POD on it is load_reprice. */
export function isPriceable(status: LoadStatus): boolean {
  return rankOf(status) <= rankOf('delivered');
}

/** After the fee (load_reprice): pod_received, invoiced, paid. */
export function isRepriceable(status: LoadStatus): boolean {
  return status === 'podReceived' || status === 'invoiced' || status === 'paid';
}

/** The itinerary can still change (load_stop_add refuses canceled and paid). */
export function stopsEditable(status: LoadStatus): boolean {
  return status !== 'canceled' && status !== 'paid';
}
